import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnChanges, OnDestroy, SimpleChanges, inject, input, output, viewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';

import { ComissaoAtaModeloResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { ComissaoAtividadeSalvarRequest, ComissaoSituacaoAtividadeResponse } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.model';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionInput } from '@/core/permissions/permission.model';
import { AtividadeAta, OpcaoFiltroComissao, ParticipanteAta, ReuniaoComissao, SituacaoPresencaAta } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { PesquisarPessoaCustomComponent } from '@/shared/components/pessoa/pesquisar-pessoa-custom/pesquisar-pessoa-custom';
import {
    AssinantesAta,
    OPCOES_PRESENCA_CONVIDADO,
    OPCOES_PRESENCA_MEMBRO,
    TipoImpressaoAta,
    TipoParticipanteAta,
    classeSituacaoAta,
    criarHtmlImpressaoDocumento,
    escapeHtml,
    iconeSituacaoAta,
    normalizarTextoAta,
    rotuloPresencaAta,
    valorPresencaAta
} from './ata-reuniao.presenter';
import { AtaAtividadesComponent } from './ata-atividades/ata-atividades';
import { AtaTabelaParticipantesComponent } from './ata-tabela-participantes/ata-tabela-participantes';
import { AtaDialogImportanteComponent } from './ata-dialog-importante/ata-dialog-importante';
import { AtaDialogPresencaComponent } from './ata-dialog-presenca/ata-dialog-presenca';
import { AtaDialogGestoresComponent, GestoresChavesAta } from './ata-dialog-gestores/ata-dialog-gestores';
import { AtaPreviewComponent } from './ata-preview/ata-preview';
import { AtaDialogAtividadeComponent } from './ata-dialog-atividade/ata-dialog-atividade';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { CardContent } from '@/shared/components/card-content/card-content';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';

interface OpcaoAssistenteAta {
    label: string;
    value: string;
}

interface AlteracaoParticipanteAta {
    participante: ParticipanteAta;
    tipo: TipoParticipanteAta;
    valor: string;
}

interface SalvamentoAtaPayload {
    conteudoHtml: string;
    inImportante: string;
    dsImportante?: string;
}

interface GestoresAtaPayload {
    coordenador?: ParticipanteAta | null;
    secretario?: ParticipanteAta | null;
    assistenteValor?: string;
}

@Component({
    selector: 'app-ata-reuniao',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        EditorModule,
        ButtonModule,
        InputTextModule,
        MenuModule,
        SelectModule,
        TableModule,
        DialogModule,
        ConfirmDialogModule,
        PesquisarPessoaCustomComponent,
        CanDirective,
        AtaAtividadesComponent,
        AtaTabelaParticipantesComponent,
        AtaDialogImportanteComponent,
        AtaDialogPresencaComponent,
        AtaDialogGestoresComponent,
        AtaPreviewComponent,
        AtaDialogAtividadeComponent,
        PessoaAvatarComponent,
        CardContent,
        BotaoAcoes
    ],
    providers: [ConfirmationService],
    templateUrl: './ata-reuniao.html',
    styleUrls: ['./ata-reuniao.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AtaReuniaoComponent implements OnChanges, OnDestroy {
    private readonly formBuilder = inject(FormBuilder);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly cdr = inject(ChangeDetectorRef);

    // --- Inputs de sinal (dados da reunião/ata) ---
    /** Reunião cuja ata está sendo editada. */
    readonly reuniao = input<ReuniaoComissao | null>(null);
    /** Opções de assistente para o cabeçalho da ata. */
    readonly opcoesAssistentes = input<OpcaoFiltroComissao[]>([]);
    /** HTML atual do documento da ata. */
    readonly documentoAtaHtml = input('');
    /** Texto do modelo a inserir no início da ata. */
    readonly textoInicioAtaModelo = input('');
    /** Contador-gatilho que dispara a inserção do início da ata. */
    readonly gatilhoInsercaoInicioAta = input(0);
    /** Texto do modelo a inserir na finalização da ata. */
    readonly textoFinalizacaoAtaModelo = input('');
    /** Contador-gatilho que dispara a inserção da finalização da ata. */
    readonly gatilhoInsercaoFinalizacaoAta = input(0);
    /** Membros da comissão (presença/função). */
    readonly membros = input<ParticipanteAta[]>([]);
    /** Convidados da reunião. */
    readonly convidados = input<ParticipanteAta[]>([]);
    /** Atividades vinculadas à reunião. */
    readonly atividades = input<AtividadeAta[]>([]);
    /** Situações possíveis das atividades. */
    readonly situacoesAtividade = input<ComissaoSituacaoAtividadeResponse[]>([]);
    /** Modelos de ata disponíveis. */
    readonly modelosAta = input<ComissaoAtaModeloResponse[]>([]);
    /** Intervalo (s) do autosave; 0 desliga. */
    readonly autosaveIntervaloSegundos = input(0);
    /** Pausa o autosave. */
    readonly autosavePausado = input(false);
    /** Mensagem de conflito de edição da ata. */
    readonly conflitoAtaMensagem = input('');

    // --- Outputs de sinal (ações da ata) ---
    /** Pede recarregar a ata. */
    readonly recarregarAtaSolicitado = output<void>();
    /** Volta para a listagem. */
    readonly voltar = output<void>();
    /** Solicita inserir o início da ata. */
    readonly solicitarInicioAta = output<void>();
    /** Solicita inserir a finalização da ata. */
    readonly solicitarFinalizacaoAta = output<void>();
    /** Salva a ata. */
    readonly salvar = output<SalvamentoAtaPayload>();
    /** Autosave da ata. */
    readonly autosave = output<SalvamentoAtaPayload>();
    /** Modelo de ata selecionado. */
    readonly modeloSelecionado = output<number>();
    /** Baixa o documento da ata. */
    readonly baixarDocumento = output<void>();
    /** Abre a edição da reunião. */
    readonly editarReuniao = output<void>();
    /** Gestores (coordenador/assistente) alterados. */
    readonly gestoresAlterados = output<GestoresAtaPayload>();
    /** Assistente alterado. */
    readonly assistenteAlterado = output<string>();
    /** Presença/função de um participante alterada. */
    readonly participanteAlterado = output<{ participante: ParticipanteAta; tipo: TipoParticipanteAta; inPresenca?: string; inFuncao?: string }>();
    /** Convidado adicionado. */
    readonly convidadoAdicionado = output<{ idSolicitante: number; ufSgUf: string }>();
    /** Convidado removido. */
    readonly convidadoRemovido = output<ParticipanteAta>();
    /** Atividade criada/editada. */
    readonly atividadeSalva = output<{ idTarefa?: number; request: ComissaoAtividadeSalvarRequest }>();
    /** Atividade excluída. */
    readonly atividadeExcluida = output<number>();
    /** Flag "resolvida" de uma atividade alterada. */
    readonly atividadeResolvidaAlterada = output<{ idTarefa: number; resolvida: boolean }>();

    protected readonly roleAtasEditar = PERMISSOES.comissoes.atas.editar;
    protected readonly roleDesignarAssistente = PERMISSOES.comissoes.reunioes.designarAssistente;
    protected readonly roleGerenciarConvidados = PERMISSOES.comissoes.atas.gerenciarConvidados;
    protected readonly roleAtividadesCriar = PERMISSOES.comissoes.atividades.criar;

    readonly legendaPresenca: Array<{ label: string; situacao: SituacaoPresencaAta }> = [
        { label: 'Presente', situacao: 'presente' },
        { label: 'Virtual', situacao: 'virtual' },
        { label: 'Ausente', situacao: 'ausente' },
        { label: 'Justificado', situacao: 'justificado' },
        { label: 'Secretário', situacao: 'secretario' },
        { label: 'Coordenador', situacao: 'coordenador' }
    ];
    /** Submenu com os modelos de ata cadastrados (aberto pelo item "Modelos cadastrados"). */
    itensModelosAta: MenuItem[] = [];

    private readonly menuModelosRef = viewChild<Menu>('menuModelos');

    protected readonly EInconPos = EInconPos;

    /** Ações secundárias do documento no botão "Opções" (recriado em recriarMenuOpcoes conforme permissão/modelos). */
    protected itensOpcoesDocumento: (MenuItem & { permission?: PermissionInput })[] = [];

    readonly formularioAta = this.formBuilder.nonNullable.group({
        assistente: '',
        buscaParticipante: '',
        conteudoAta: ''
    });

    exibirDialogAtividade = false;
    atividadeEmEdicao: AtividadeAta | null = null;
    exibirPreviewAta = false;
    tipoPreview: TipoImpressaoAta = 'minuta';
    documentoPreviewHtml = '';

    get responsaveisPossiveis(): Array<{ label: string; id: number; uf: string }> {
        const lista = [...this.membros(), ...this.convidados()];
        return lista.map((p) => ({
            label: `${p.nome} (${p.uf})`,
            id: p.idSolicitante,
            uf: p.uf
        }));
    }

    ataImportante = false;
    textoImportante = '';
    atividadeResolvida = false;
    atividadeSelecionada: AtividadeAta | null = null;
    private codigoReuniaoAtual = '';

    exibirDialogPresenca = false;
    participanteSelecionado: ParticipanteAta | null = null;
    tipoParticipanteSelecionado: TipoParticipanteAta | null = null;
    presencaSelecionada = '';
    opcoesDialogPresenca: OpcaoAssistenteAta[] = [];
    pesquisaConvidadoVisivel = false;
    exibirDialogImportante = false;
    exibirDialogGestores = false;
    gestoresIniciais: GestoresChavesAta = { coordenador: '', secretario: '', assistente: '' };

    private autosaveTimer: ReturnType<typeof setInterval> | null = null;
    private ultimoConteudoAutosave = '';

    ngOnChanges(changes: SimpleChanges): void {
        const codigoReuniao = this.reuniao()?.codigo ?? '';
        const mudouReuniao = codigoReuniao !== this.codigoReuniaoAtual;
        const mudouDocumentoAta = !!changes['documentoAtaHtml'];
        const mudouGatilhoInicioAta = !!changes['gatilhoInsercaoInicioAta'];
        const mudouGatilhoFinalizacaoAta = !!changes['gatilhoInsercaoFinalizacaoAta'];

        if (mudouReuniao || changes['opcoesAssistentes']) {
            this.codigoReuniaoAtual = codigoReuniao;
            this.preencherCamposIniciais();
        } else if (changes['reuniao']) {
            this.ataImportante = this.reuniao()?.ataMarcada ?? false;
            this.textoImportante = this.reuniao()?.dsImportante?.trim() ?? '';
            this.ataImportante = this.ataImportante || !!this.textoImportante;
        }

        this.aplicarEstadoEdicao();
        this.recriarMenuOpcoes();

        if (mudouReuniao || mudouDocumentoAta) {
            this.aplicarDocumentoAtaHtml();
        }

        if (changes['autosavePausado']) {
            this.configurarAutosave();
        }

        if (mudouGatilhoInicioAta && this.gatilhoInsercaoInicioAta() > 0) {
            this.aplicarTextoInicioAtaModelo();
        }

        if (mudouGatilhoFinalizacaoAta && this.gatilhoInsercaoFinalizacaoAta() > 0) {
            this.aplicarTextoFinalizacaoAtaModelo();
        }

        if (mudouReuniao || changes['autosaveIntervaloSegundos']) {
            this.configurarAutosave();
        }

        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        this.limparAutosave();
    }

    get tituloReuniao(): string {
        const reuniao = this.reuniao();
        if (!reuniao) {
            return 'REUNIÃO: -';
        }

        const dataInicio = reuniao.dataInicio
            ? new Intl.DateTimeFormat('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
              }).format(reuniao.dataInicio)
            : 'data não informada';

        return `REUNIÃO: ${reuniao.nomeComissao} - ${dataInicio}, ${reuniao.modalidade}`;
    }

    get protocoloReuniao(): string {
        return this.reuniao()?.protocolo || '---';
    }

    get permiteAlteracao(): boolean {
        const idStatusAta = this.reuniao()?.idStatusAta;
        return idStatusAta !== 4 && idStatusAta !== 5;
    }

    get assistentesDisponiveis(): OpcaoAssistenteAta[] {
        return this.opcoesAssistentes()
            .filter((opcao) => opcao.valor !== 'todos')
            .map((opcao) => ({
                label: opcao.rotulo,
                value: opcao.valor
            }));
    }

    get opcoesGestoresMembros(): OpcaoAssistenteAta[] {
        return this.membros().map((membro) => ({
            label: `${membro.nome} (${membro.uf})`,
            value: this.chaveParticipante(membro)
        }));
    }

    /** Extrai o id da pessoa de uma chave "id|uf" (para a foto do avatar); null se ausente. */
    protected idDaChavePessoa(chave: string | null | undefined): number | null {
        const id = Number((chave ?? '').split('|')[0]);
        return Number.isFinite(id) && id > 0 ? id : null;
    }

    /** Extrai a UF de uma chave "id|uf". */
    protected ufDaChavePessoa(chave: string | null | undefined): string {
        return (chave ?? '').split('|')[1] ?? '';
    }

    voltarParaAcompanhamento(): void {
        this.voltar.emit();
    }

    salvarAta(): void {
        if (!this.permiteAlteracao) {
            return;
        }

        this.ultimoConteudoAutosave = this.formularioAta.controls.conteudoAta.value;
        this.salvar.emit(this.criarPayloadSalvamentoAta());
    }

    abrirPreviewAta(): void {
        this.tipoPreview = 'minuta';
        this.documentoPreviewHtml = this.montarHtmlImpressao('minuta');
        this.exibirPreviewAta = true;
    }

    selecionarTipoPreview(tipo: TipoImpressaoAta): void {
        this.tipoPreview = tipo;
        this.documentoPreviewHtml = this.montarHtmlImpressao(tipo);
    }

    baixarAta(): void {
        this.baixarDocumento.emit();
    }

    solicitarRecarregarAta(): void {
        this.recarregarAtaSolicitado.emit();
    }

    imprimirDocumento(tipo: TipoImpressaoAta): void {
        const janela = window.open('', '_blank', 'width=980,height=720');
        if (!janela) {
            return;
        }

        janela.opener = null;
        janela.document.open();
        janela.document.write(this.montarHtmlImpressao(tipo));
        janela.document.close();
        janela.focus();

        setTimeout(() => {
            janela.print();
        }, 300);
    }

    selecionarAtividade(atividade: AtividadeAta): void {
        this.atividadeSelecionada = atividade;
        this.atividadeResolvida = atividade.resolvida;
    }

    abrirDialogImportante(): void {
        if (!this.permiteAlteracao) {
            return;
        }

        this.exibirDialogImportante = true;
    }

    confirmarImportante(texto: string): void {
        if (!this.permiteAlteracao) {
            return;
        }

        this.textoImportante = texto.trim();
        this.ataImportante = !!this.textoImportante;
        this.cdr.markForCheck();
    }

    solicitarEdicaoReuniao(): void {
        this.editarReuniao.emit();
    }

    abrirDialogGestores(): void {
        if (!this.permiteAlteracao) {
            return;
        }

        this.gestoresIniciais = {
            coordenador: this.chaveParticipante(this.obterParticipantePorFuncao('C')),
            secretario: this.chaveParticipante(this.obterParticipantePorFuncao('S')),
            assistente: this.formularioAta.controls.assistente.value
        };
        this.exibirDialogGestores = true;
    }

    confirmarGestores(chaves: GestoresChavesAta): void {
        if (!this.permiteAlteracao) {
            return;
        }

        this.gestoresAlterados.emit({
            coordenador: this.obterParticipantePorChave(chaves.coordenador),
            secretario: this.obterParticipantePorChave(chaves.secretario),
            assistenteValor: chaves.assistente
        });
    }

    aoAlterarAssistente(): void {
        if (!this.permiteAlteracao) {
            return;
        }

        this.assistenteAlterado.emit(this.formularioAta.controls.assistente.value);
    }

    aoAlterarParticipante(alteracao: AlteracaoParticipanteAta): void {
        if (!this.permiteAlteracao) {
            return;
        }

        const valor = alteracao.valor;
        const payload = {
            participante: alteracao.participante,
            tipo: alteracao.tipo,
            inPresenca: undefined as string | undefined,
            inFuncao: undefined as string | undefined
        };

        if (valor === 'C' || valor === 'S') {
            payload.inFuncao = valor;
        } else {
            payload.inPresenca = valor;
            payload.inFuncao = valor === 'A' || valor === 'J' ? '' : alteracao.participante.inFuncao;
        }

        this.participanteAlterado.emit(payload);
    }

    abrirDialogPresenca(participante: ParticipanteAta, tipo: TipoParticipanteAta): void {
        if (!this.permiteAlteracao) {
            return;
        }
        this.participanteSelecionado = participante;
        this.tipoParticipanteSelecionado = tipo;
        this.presencaSelecionada = valorPresencaAta(participante);
        this.opcoesDialogPresenca = tipo === 'M' ? OPCOES_PRESENCA_MEMBRO : OPCOES_PRESENCA_CONVIDADO;
        this.exibirDialogPresenca = true;
    }

    confirmarAlteracaoPresenca(valor: string): void {
        if (this.participanteSelecionado && this.tipoParticipanteSelecionado) {
            this.aoAlterarParticipante({
                participante: this.participanteSelecionado,
                tipo: this.tipoParticipanteSelecionado,
                valor
            });
        }
    }

    abrirDialogConvidado(): void {
        if (!this.permiteAlteracao) {
            return;
        }
        this.pesquisaConvidadoVisivel = true;
    }

    adicionarConvidadoSelecionado(pessoa: PessoaDomain): void {
        if (!this.permiteAlteracao) {
            return;
        }

        const idSolicitante = Number(pessoa.id);
        const ufSgUf = (pessoa.uf ?? '').trim().toUpperCase();
        if (!Number.isFinite(idSolicitante) || idSolicitante <= 0 || ufSgUf.length !== 2) {
            return;
        }

        this.convidadoAdicionado.emit({ idSolicitante, ufSgUf });
        this.pesquisaConvidadoVisivel = false;
    }

    removerConvidado(convidado: ParticipanteAta, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();

        if (!this.permiteAlteracao) {
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja remover o convidado ${convidado.nome}?`,
            header: 'Remover convidado',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remover',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.convidadoRemovido.emit(convidado);
            }
        });
    }

    inserirParticipanteNaAta(participante: ParticipanteAta, tipo: TipoParticipanteAta): void {
        if (!this.permiteAlteracao) {
            return;
        }

        const rotuloPresenca = rotuloPresencaAta(participante, tipo);
        this.inserirTextoNoFimConteudoAta(`${participante.nome} (${participante.uf}) - ${rotuloPresenca}`);
    }

    inserirTodosParticipantesNaAta(): void {
        if (!this.permiteAlteracao) {
            return;
        }

        const participantes = [...this.membros(), ...this.convidados()];
        const grupos = [
            { titulo: 'Presentes', valores: ['P', 'C', 'S'] },
            { titulo: 'Presentes virtualmente', valores: ['V'] },
            { titulo: 'Ausências justificadas', valores: ['J'] }
        ];
        const blocos = grupos
            .map((grupo) => {
                const nomes = participantes.filter((participante) => grupo.valores.includes(this.valorPresencaParaListagem(participante))).map((participante) => `${participante.nome} (${participante.uf})`);
                return nomes.length ? `${grupo.titulo}: ${nomes.join('; ')}` : '';
            })
            .filter(Boolean);

        if (blocos.length) {
            this.inserirBlocosNoFimConteudoAta(blocos);
        }
    }

    iconeSituacao(situacao: SituacaoPresencaAta): string {
        return iconeSituacaoAta(situacao);
    }

    classeSituacao(situacao: SituacaoPresencaAta): string {
        return classeSituacaoAta(situacao);
    }

    private preencherCamposIniciais(): void {
        const assistentePadrao = this.obterAssistentePadrao();

        this.formularioAta.patchValue(
            {
                assistente: assistentePadrao,
                buscaParticipante: '',
                conteudoAta: ''
            },
            { emitEvent: false }
        );

        this.ataImportante = this.reuniao()?.ataMarcada ?? false;
        this.textoImportante = this.reuniao()?.dsImportante?.trim() ?? '';
        this.ataImportante = this.ataImportante || !!this.textoImportante;
    }

    private aplicarDocumentoAtaHtml(): void {
        const conteudo = this.documentoAtaHtml().trim();
        this.formularioAta.patchValue(
            {
                conteudoAta: conteudo
            },
            { emitEvent: false }
        );
        this.ultimoConteudoAutosave = conteudo;
    }

    private aplicarTextoInicioAtaModelo(): void {
        const textoModelo = this.textoInicioAtaModelo().trim();
        if (!textoModelo) {
            return;
        }

        this.inserirTextoNoInicioConteudoAta(textoModelo);
    }

    private aplicarTextoFinalizacaoAtaModelo(): void {
        const textoModelo = this.textoFinalizacaoAtaModelo().trim();
        if (!textoModelo) {
            return;
        }

        this.inserirTextoNoFimConteudoAta(textoModelo);
    }

    private inserirTextoNoInicioConteudoAta(texto: string): void {
        const conteudoAtual = this.formularioAta.controls.conteudoAta.value.trim();
        const blocoHtml = `<p>${escapeHtml(texto)}</p>`;
        const conteudoAtualizado = conteudoAtual ? `${blocoHtml}<p><br></p>${conteudoAtual}` : blocoHtml;

        this.formularioAta.patchValue(
            {
                conteudoAta: conteudoAtualizado
            },
            { emitEvent: false }
        );
    }

    private inserirTextoNoFimConteudoAta(texto: string): void {
        this.inserirBlocosNoFimConteudoAta([texto]);
    }

    private inserirBlocosNoFimConteudoAta(blocos: string[]): void {
        const conteudoAtual = this.formularioAta.controls.conteudoAta.value.trim();
        const blocoHtml = blocos.map((texto) => `<p>${escapeHtml(texto)}</p>`).join('');
        const conteudoAtualizado = conteudoAtual ? `${conteudoAtual}<p><br></p>${blocoHtml}` : blocoHtml;

        this.formularioAta.patchValue(
            {
                conteudoAta: conteudoAtualizado
            },
            { emitEvent: false }
        );
    }

    private criarPayloadSalvamentoAta(): SalvamentoAtaPayload {
        const dsImportante = this.textoImportante.trim();
        return {
            conteudoHtml: this.formularioAta.controls.conteudoAta.value,
            inImportante: this.ataImportante || !!dsImportante ? 'S' : 'N',
            dsImportante: dsImportante || undefined
        };
    }

    private obterAssistentePadrao(): string {
        const opcoes = this.assistentesDisponiveis;
        if (!opcoes.length) {
            return '';
        }

        const assistenteReuniao = normalizarTextoAta(this.reuniao()?.assistente);
        const opcaoCorrespondente = opcoes.find((opcao) => normalizarTextoAta(opcao.label) === assistenteReuniao);
        if (opcaoCorrespondente) {
            return opcaoCorrespondente.value;
        }

        return opcoes[0].value;
    }

    private valorPresencaParaListagem(participante: ParticipanteAta): string {
        const presenca = normalizarTextoAta(participante.inPresenca);
        if (presenca) {
            return presenca;
        }
        return valorPresencaAta(participante);
    }

    private chaveParticipante(participante?: ParticipanteAta | null): string {
        return participante ? `${participante.idSolicitante}|${participante.uf}` : '';
    }

    private obterParticipantePorFuncao(funcao: 'C' | 'S'): ParticipanteAta | null {
        return this.membros().find((membro) => normalizarTextoAta(membro.inFuncao) === funcao) ?? null;
    }

    private obterParticipantePorChave(chave: string): ParticipanteAta | null {
        if (!chave) {
            return null;
        }
        const [id, uf] = chave.split('|');
        const idSolicitante = Number(id);
        return this.membros().find((membro) => membro.idSolicitante === idSolicitante && membro.uf === uf) ?? null;
    }

    private obterNomeAssistenteSelecionado(): string {
        const valor = this.formularioAta.controls.assistente.value;
        return this.assistentesDisponiveis.find((opcao) => opcao.value === valor)?.label || this.reuniao()?.assistente || '';
    }

    /** Resolve os nomes dos assinantes (coordenador/secretário/assistente) para o documento. */
    private montarAssinantes(): AssinantesAta {
        return {
            coordenador: this.obterParticipantePorFuncao('C')?.nome || this.reuniao()?.nomeCoordenador || '-',
            secretario: this.obterParticipantePorFuncao('S')?.nome || '-',
            assistente: this.obterNomeAssistenteSelecionado() || '-'
        };
    }

    /** Monta o HTML de impressão/preview delegando a geração pura ao presenter. */
    private montarHtmlImpressao(tipo: TipoImpressaoAta): string {
        return criarHtmlImpressaoDocumento(tipo, this.reuniao(), this.formularioAta.controls.conteudoAta.value, this.montarAssinantes());
    }

    private aplicarEstadoEdicao(): void {
        if (this.permiteAlteracao) {
            this.formularioAta.enable({ emitEvent: false });
        } else {
            this.formularioAta.disable({ emitEvent: false });
            this.limparAutosave();
        }
    }

    /**
     * Recria o menu "Opções": os blocos "Modelo"/"Finalização" e as impressões ficam como itens diretos
     * (não em submenus); "Modelos cadastrados" só aparece quando há modelos salvos.
     */
    private recriarMenuOpcoes(): void {
        const gerenciarModelos = PERMISSOES.comissoes.atas.gerenciarModelos;
        const bloqueado = !this.permiteAlteracao;

        this.itensModelosAta = (this.modelosAta() ?? [])
            .filter((modelo) => typeof modelo.idModeloAta === 'number' && Number.isFinite(modelo.idModeloAta))
            .map((modelo) => ({
                label: modelo.dsModeloAta?.trim() || `Modelo ${modelo.idModeloAta}`,
                icon: 'pi pi-file',
                disabled: bloqueado,
                command: () => this.modeloSelecionado.emit(modelo.idModeloAta!)
            }));

        const itens: (MenuItem & { permission?: PermissionInput })[] = [
            { label: 'Modelo', icon: 'pi pi-file-edit', styleClass: 'bg-indigo-500', permission: gerenciarModelos, disabled: bloqueado, command: () => this.solicitarInicioAta.emit() },
            { label: 'Finalização', icon: 'pi pi-file-check', styleClass: 'bg-indigo-500', permission: gerenciarModelos, disabled: bloqueado, command: () => this.solicitarFinalizacaoAta.emit() }
        ];

        if (this.itensModelosAta.length) {
            itens.push({ label: 'Modelos cadastrados', icon: 'pi pi-copy', styleClass: 'bg-indigo-500', permission: gerenciarModelos, command: (evento) => this.menuModelosRef()?.toggle(evento?.originalEvent as Event) });
        }

        itens.push(
            { label: 'Minuta', icon: 'pi pi-eye', styleClass: 'bg-blue-500', command: () => this.abrirPreviewAta() },
            { label: 'Ata', icon: 'pi pi-print', styleClass: 'bg-slate-500', command: () => this.imprimirDocumento('ata') },
            { label: 'Minuta', icon: 'pi pi-print', styleClass: 'bg-slate-500', command: () => this.imprimirDocumento('minuta') },
            { label: 'Reunião', icon: 'pi pi-calendar', styleClass: 'bg-teal-500', permission: PERMISSOES.comissoes.reunioes.editar, command: () => this.solicitarEdicaoReuniao() },
            { label: 'Baixar RTF', icon: 'pi pi-download', styleClass: 'bg-green-500', command: () => this.baixarAta() }
        );

        this.itensOpcoesDocumento = itens;
    }

    private configurarAutosave(): void {
        this.limparAutosave();
        if (!this.permiteAlteracao || this.autosavePausado() || !this.autosaveIntervaloSegundos() || this.autosaveIntervaloSegundos() <= 0) {
            return;
        }

        this.autosaveTimer = setInterval(() => {
            if (!this.permiteAlteracao) {
                this.limparAutosave();
                return;
            }

            const conteudoAtual = this.formularioAta.controls.conteudoAta.value;
            if (conteudoAtual === this.ultimoConteudoAutosave) {
                return;
            }

            this.ultimoConteudoAutosave = conteudoAtual;
            this.autosave.emit(this.criarPayloadSalvamentoAta());
        }, this.autosaveIntervaloSegundos() * 1000);
    }

    private limparAutosave(): void {
        if (this.autosaveTimer) {
            clearInterval(this.autosaveTimer);
            this.autosaveTimer = null;
        }
    }

    abrirDialogNovaAtividade(): void {
        if (!this.permiteAlteracao) return;
        this.atividadeEmEdicao = null;
        this.exibirDialogAtividade = true;
    }

    abrirDialogEditarAtividade(atividade: AtividadeAta): void {
        if (!this.permiteAlteracao) return;
        this.atividadeSelecionada = atividade;
        this.atividadeEmEdicao = atividade;
        this.exibirDialogAtividade = true;
    }

    salvarAtividade(payload: { idTarefa?: number; request: ComissaoAtividadeSalvarRequest }): void {
        this.atividadeSalva.emit(payload);
    }

    removerAtividade(atividade: AtividadeAta): void {
        if (!this.permiteAlteracao) return;
        this.confirmationService.confirm({
            message: 'Deseja realmente excluir esta atividade?',
            header: 'Confirmar exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.atividadeExcluida.emit(atividade.idTarefa);
                if (this.atividadeSelecionada?.idTarefa === atividade.idTarefa) {
                    this.atividadeSelecionada = null;
                    this.atividadeResolvida = false;
                }
            }
        });
    }

    alternarAtividadeResolvida(resolvida: boolean): void {
        if (!this.permiteAlteracao || !this.atividadeSelecionada) return;

        this.atividadeResolvida = resolvida;
        this.atividadeSelecionada.resolvida = resolvida;

        this.atividadeResolvidaAlterada.emit({
            idTarefa: this.atividadeSelecionada.idTarefa,
            resolvida
        });
    }
}
